// Client
import Client from 'fhir-kit-client';
// React
import { Dispatch, FunctionComponent, SetStateAction, useState, useEffect, useReducer } from "react";
// Component
import { Title, FhirStatus, StatusAlert, StatusIcon } from '@fyrstain/fhir-front-library';
import validatorProfilesMap from './validatorProfilesMap';
// React Bootstrap
import { Form, Table, Accordion } from 'react-bootstrap';
// FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faRotate, faTrash } from '@fortawesome/free-solid-svg-icons';
// Translation
import i18n from 'i18next';
// Fhir
import { Bundle, Device } from 'fhir/r5';

////////////////////////////////////////
//             Interfaces             //
////////////////////////////////////////

interface ProfileSectionConfiguration {
    fhirClient: Client,
    profiles: Validation[],
    setProfiles: Dispatch<SetStateAction<Validation[]>>,
    isProfileUsed: (profileId: string) => boolean,
}

///////////////////////////////////////
//                Type               //
///////////////////////////////////////

export type Validation = {
    internalId: string,
    id: number,
    validator: string,
    profileName: string,
    profileUrl: string
}

const ProfilesSection: FunctionComponent<ProfileSectionConfiguration> = (configs) => {

    //////////////////////////////////
    //            States            //
    //////////////////////////////////

    const [counter, incrementCounter] = useReducer(x => x + 1, 0);

    // The list of validators
    const [validators, setValidators] = useState<{ id: string, name: string }[]>([]);

    // TODO use when the backend is ready to retrieve the validator by the profiles for the edition mode
    // Profile options, based on validator choice
    // const [profileOptions, setProfileOptions] = useState<{ [validatorId: string]: { name: string; url: string }[] }>({});

    /////////////////////////////////////
    //            UseEffect            //
    /////////////////////////////////////

    /**
     * Load validators while mounting the component
     */
    useEffect(() => {
        loadValidators();
    }, []);

    /////////////////////////////////////
    //            Functions            //
    /////////////////////////////////////

    /**
     * Load Validators from the back to populate the validator select field.
     *
     * @returns the promise of a Device
     * 
     */
    async function loadValidators() {
        try {
            const response = await configs.fhirClient.search({
                resourceType: "Device",
                searchParams: { "type": "http://fyrstain.com/pdt/ValueSet/systemTypes|validator" }
            });
            if (response.resourceType !== 'Bundle') {
                throw Error(response.statusText);
            }
            const bundle: Bundle = response as Bundle;
            setValidators(bundle.entry?.filter(entry => entry.resource?.resourceType === "Device")
                .map((entry) => ({
                    id: (entry.resource as Device)?.id ?? '',
                    name: (entry.resource as Device)?.displayName ?? ''
                })) ?? []);
        } catch (error) {
            console.log(error);
        }
    }

    /**
     * Handle the change of the profile fields
     * 
     * @param index 
     *  
     */
    const handleProfileChange = (index: number) => (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value;
        const newProfiles = configs.profiles.map((item, i) => {
            if (i === index) {
                return {
                    ...item,
                    profileUrl: value,
                    profileName: validatorProfilesMap[item.validator]?.find((profile) => profile.url === value)?.name ?? '',
                };
            }
            return item;
        });
        configs.setProfiles(newProfiles);
    };

    /**
     * Handle the change of the profiles validation fields
     * 
     * @param index 
     * @param field 
     *  
     */
    const handleValidatorChange = (index: number) => (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value;
        const newProfiles = configs.profiles.map((item, i) => {
            if (i === index) {
                return {
                    ...item,
                    validator: value,
                    profileUrl: '',
                    profileName: '',
                };
            }
            return item;
        });
        configs.setProfiles(newProfiles);
    };

    /**
     * Load Profiles by validator from the back to populate the profile select field.
     *
     */
    // async function loadProfilesByValidator(validatorId: string) {
    //     try {
    //         const response = await configs.fhirClient.operation({
    //             name: '$profiles',
    //             method: 'GET',
    //             input: { "validatorId": validatorId }
    //         });
    //         const results = response as { parameter?: { name: string; valueString?: string }[] };
    //         const profiles: { name: string, url: string }[] = [];
    //         results.parameter?.forEach((param: { name: string; valueString?: string }) => {
    //             profiles.push({ name: param.name, url: param.valueString ?? '' });
    //         });
    //         setProfileOptions(prev => ({ ...prev, [validatorId]: profiles }));
    //     } catch (error) {
    //         // TODO See how we handle the error !
    //         console.log(error);
    //     }
    // }

    /**
     * Add a profile row
     * 
     */
    const addRow = () => {
        incrementCounter();
        const newItem = { internalId: `validation-${counter}`, id: configs.profiles.length, validator: '', profileName: '', profileUrl: '' };
        configs.setProfiles(prevItems => [...prevItems, newItem]);
    };

    /** 
     * Refresh a profile row
     * 
     * @param index 
     * 
     */
    const refreshRow = (internalId: string) => {
        const newItems = configs.profiles.map((item) => {
            if (item.internalId === internalId) {
                return { ...item, validator: '', profileName: '', profileUrl: '' };
            }
            return item;
        });
        configs.setProfiles(newItems as Validation[]);
    };

    /**
     * Delete a profile row
     * 
     * @param index 
     * 
     */
    const deleteRow = (internalId: string) => {
        if (configs.isProfileUsed(internalId)) {
            window.alert(i18n.t('errormessage.profilevalidationusedbyassert'));
            return;
        }
        const confirmDelete = window.confirm(i18n.t('confirmationmessage.deleteprofilevalidation'));
        if (!confirmDelete) {
            return;
        }
        // Delete the element with the specified internalId
        const newItems = configs.profiles.filter(item => item.internalId !== internalId).map((item, i) => {
            // Update the id to maintain the sequence
            return { ...item, id: i };
        });
        // Update the list for parent
        configs.setProfiles(newItems as Validation[]);
    };

    ///////////////////////////////////////
    //              Content              //
    ///////////////////////////////////////

    return <Accordion
        defaultActiveKey="0"
    >
        <Accordion.Item eventKey="0">
            <Accordion.Header>
                <Title
                    level={2}
                    content={i18n.t('title.profilesvalidation')}
                />
            </Accordion.Header>
            <Accordion.Body>
                {(configs.profiles.length === 0) &&
                    <StatusAlert
                        status={FhirStatus.information}
                    >
                        <div className="d-flex align-items-center gap-2">
                            <StatusIcon status={FhirStatus.information} />
                            <strong>
                                {i18n.t('infomessage.noprofilevalidation')}
                            </strong>
                        </div>
                    </StatusAlert>
                }
                {(configs.profiles.length > 0) &&
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th className="col-1">
                                    ID
                                </th>
                                <th className="col-5">
                                    {i18n.t('title.validator')}
                                </th>
                                <th className="col-5">
                                    {i18n.t('general.profile')}
                                </th>
                                <th>
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {configs.profiles.map((profile, index) => (
                                <tr key={index}>
                                    <td className="align-middle">
                                        {profile.id}
                                    </td>
                                    <td>
                                        <Form.Select
                                            required
                                            value={profile.validator}
                                            onChange={handleValidatorChange(index)}
                                        >
                                            <option value="">{i18n.t('placeholder.validator')}</option>
                                            {validators.map((validator, idx) => (
                                                <option
                                                    key={idx}
                                                    value={validator.id}>
                                                    {validator.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </td>
                                    <td>
                                        <Form.Select
                                            required
                                            value={profile.profileUrl}
                                            onChange={handleProfileChange(index)}
                                            disabled={!profile.validator || profile.validator === ''}
                                        >
                                            <option value="">{i18n.t('placeholder.profile')}</option>
                                            {validatorProfilesMap[profile.validator]?.map((profileOption, idx) => (
                                                <option key={idx} value={profileOption.url}>
                                                    {profileOption.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </td>
                                    <td className="align-middle">
                                        <FontAwesomeIcon
                                            icon={faRotate}
                                            className="actionIcon"
                                            onClick={() => refreshRow(profile.internalId)}
                                        />
                                        <FontAwesomeIcon
                                            icon={faTrash}
                                            className="actionIcon"
                                            onClick={() => deleteRow(profile.internalId)}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                }
                <div>
                    <div
                        className="actionIcon d-flex flex-wrap align-items-center gap-2 w-11rem"
                        onClick={() => addRow()}
                    >
                        <FontAwesomeIcon
                            icon={faPlus}
                            role="button"
                        />
                        <strong
                            role="button"
                        >
                            {i18n.t('button.addprofilevalidation')}
                        </strong>
                    </div>
                </div>
            </Accordion.Body>
        </Accordion.Item>
    </Accordion >
};

export default ProfilesSection;